"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { X } from "lucide-react";

// Checkout flow components
import { CheckoutRegular } from "./flows/checkout-regular";
import { CheckoutTerm } from "./flows/checkout-term";
import { CheckoutTrial } from "./flows/checkout-trial";
import { CheckoutDropIn } from "./flows/checkout-dropin";
import { CheckoutAppointment } from "./flows/checkout-appointment";
import { CheckoutRental } from "./flows/checkout-rental";
import { CheckoutBundle } from "./flows/checkout-bundle";

// UI components
import { BookingOptionsGrid } from "./booking-options-grid";
import { CheckoutHeaderActionsProvider } from "./checkout-panel";
import {
  fetchPoolAvailability,
  isVariableDurationFlow,
  poolCapsByKey,
  type CheckoutPoolAvailability,
} from "../../lib/checkout/pool-availability";

/** Mirrors parsePoolTicketTypeIds' cap on the server; kept in step by hand. */
const MAX_POOL_CAP_TICKET_IDS = 8;
import { CheckoutThemeProvider, type PartialCheckoutTheme } from "./theme";
import { CheckoutLoginGate } from "./checkout-login-gate";
import type { BasketChoiceNotice } from "./basket-choice-step";
import { shouldGateCheckout } from "./utils/should-gate-checkout";

// Shared API and types
import { createCourseDataLoader } from "../../lib/checkout/course-data-loader";
import {
  loadMoreDates,
  loadMoreTermDates,
  loadMoreTrialDates,
  loadMoreDropInDates,
  fetchAvailableSlots,
  confirmFreeBooking,
  validateDiscount,
  pollBasketOrderRecord,
  buildPostSuccessUrl,
  type CheckoutApiConfig,
  type CourseWithBundlesResponse,
  type BundleData,
  type BookingOptionType,
  type ActiveCheckout,
  type DiscountResult,
} from "../../lib/checkout";
import { buildBookingOptions } from "../../lib/checkout/build-booking-options";
import { stampRequiresApproval } from "../../lib/checkout/request-to-book";
import { CHECKOUT_FRAME_CLASSES } from "../../lib/checkout/frame";

import type {
  CheckoutParticipantField,
  ParticipantPayloadEntry,
} from "./participants-step";

export type CheckoutOrchestratorProps = {
  listingId: string;
  apiConfig: CheckoutApiConfig;
  primaryColor?: string;
  onClose: () => void;
  onSuccess: () => void;
  onError?: (error: Error | string) => void;
  /** Optional: Render wrapper for the checkout content (e.g., Portal, Modal) */
  renderWrapper?: (children: React.ReactNode) => React.ReactNode;
  /** Optional: Custom header component */
  renderHeader?: (props: {
    activeCheckout: ActiveCheckout;
    onBack: () => void;
    onClose: () => void;
  }) => React.ReactNode;
  /** Widget base URL for 3DS redirect return (e.g., "https://botanicalboys.live.baluu.co.uk") */
  /**
   * Whether to allow inline receipt (no redirect after payment).
   * Only works for direct charges (standard Stripe Connect accounts).
   * For destination charges, always redirects regardless of this setting.
   * Default: false (always redirect, preserves widget behavior)
   */
  allowInlineReceipt?: boolean;
  /**
   * When true, clicking the checkout panel's close button will close the entire overlay
   * instead of going back to the booking options list.
   * Useful for webcomponents where there's no options list to go back to.
   * Default: false
   */
  closeOverlayOnPanelDismiss?: boolean;
  /**
   * When true, the active checkout flow's panel header shows a Back button
   * (left of the title), fed via CheckoutHeaderActionsProvider. Back dismisses
   * the overlay for single-option experiences (the options grid was
   * auto-skipped, so there's nowhere to return to) and returns to the grid
   * when multiple options exist. Off by default — other consumers keep the
   * orchestrator's default-header back link unchanged.
   */
  enablePanelBackButton?: boolean;
  /**
   * Callback fired when checkout step changes.
   * Useful for scrolling to top or other step-change effects.
   */
  onStepChange?: (step: string) => void;
  /**
   * When true, footer is not sticky - flows after content (for iframe embeds).
   * Default: false (sticky footer for webcomponents and normal widget)
   */
  isEmbedMode?: boolean;
  /**
   * Pre-select a booking type on mount (skips options screen).
   * Useful for deep links like /course/xyz?checkout=regular
   */
  initialBookingType?: BookingOptionType;
  /**
   * Pre-select a specific session on mount (skips date selection).
   * Used together with initialBookingType for deep links.
   */
  initialSessionId?: string;
  /**
   * Waiting-list claim-link hold id (?offer=). Forwarded to the dates fetch
   * so the claimer's OWN held seats aren't shown as sold out to them — other
   * customers (no offer param) see held seats as taken.
   */
  offerRef?: string;
  /**
   * Pre-select a bundle by ID.
   */
  initialBundleId?: string;
  /**
   * When true (set automatically when initialBookingType + initialSessionId are provided),
   * hides "Back to options" and prevents going back to date selection.
   * Used for timetable "Book Now" flow.
   */
  isDirectBooking?: boolean;
  /**
   * Callback for joining waiting list when no booking options are available.
   * When provided, a "Join Waiting List" button will be shown in the empty state.
   * Receives the sold-out session id when the empty state is for a specific
   * session (so the popup can offer "this date"), and the term's event id for a
   * full TERM card (so the popup opens in term mode); both undefined for
   * course-wide contexts like "Can't make these dates?".
   */
  onJoinWaitingList?: (sessionId?: string, eventId?: string) => void;
  /**
   * Callback for opening terms dialog externally (e.g., in a Shadow DOM portal for webcomponents).
   * When provided, clicking "Cancellation Policy" will call this instead of opening the built-in dialog.
   * The callback receives the terms data needed to render the dialog.
   */
  onOpenTermsDialog?: (data: {
    termsSettings?: {
      termsAndConditions?: { mandatoryTickBox?: boolean; text?: string; updated?: string };
      cancellationPolicy?: { canCancel: boolean; cancellationTiers: Array<{ daysBefore: number; hoursBefore: number; refundPercentage: number }> };
      reschedulePolicy?: { canReschedule: boolean; rescheduleDaysBefore?: number; rescheduleHoursBefore?: number };
    };
    customTerms?: { privateEvents?: string | null; products?: string | null; giftCards?: string | null; useCustomGiftCardTerms?: "default" | "custom" | "both" };
    timezone?: string;
    ticketPolicies?: Array<{ ticketName: string; policy: any }>;
  }) => void;
  /**
   * Whether multi-date selection is enabled (requires basket).
   * When enabled, customers can select multiple dates and add them all to basket.
   */
  isMultiDateEnabled?: boolean;
  /**
   * Maximum number of dates a customer can select (multi-date mode).
   * Default: 10
   */
  maxDateSelections?: number;
  /**
   * Participants (attendees) — the school's checkout-eligible participant
   * field definitions are self-fetched by the orchestrator (same loading gate
   * as course data) once `school.participantsEnabled` is known, so hosts no
   * longer supply them. `existingParticipants` (the buyer's saved
   * participants when signed in) is still auth-bound and host-supplied.
   */
  existingParticipants?: import("./participants-step").ExistingParticipant[];
  /**
   * The signed-in customer's own identity. Used only to name the buyer on the
   * "I'm attending" toggle and their attendee card — the server resolves who
   * the buyer is from the order, never from this. Hosts pass it when a session
   * exists; on a basket-enabled listing the buyer's details are collected
   * downstream, so without it their own card would be unlabelled.
   */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /**
   * What the buyer has already answered about themselves on another item in
   * this basket, so a second listing doesn't ask again. Host-owned because the
   * basket outlives any one checkout. Optional: without it each item stands
   * alone, and the server still reconciles at charge time.
   */
  buyerSelfAnswers?: Record<string, unknown>;
  onBuyerSelfAnswersChange?: (answers: Record<string, unknown>) => void;
  /**
   * Current number of items in the basket (for display purposes).
   */
  basketItemCount?: number;
  /**
   * Set when the host refused the last basket choice (e.g. a request-to-book
   * ticket can't share the basket with what's in it): shown on the basket
   * choice step with how to fix it, until the host clears it.
   */
  basketConflict?: BasketChoiceNotice | null;
  /**
   * Per-session quantity this customer has in their basket for this course.
   * Subtracted from displayed "spots left" so the user can't add the same
   * single-seat session twice. Defaults to empty (no adjustment).
   */
  reservedSessionQuantities?: Record<string, number>;
  /**
   * Callback when user adds an item to the basket.
   * The parent component should handle adding to the basket store.
   */
  onAddToBasket?: (itemData: {
    type: "booking";
    bookingType?: "regular" | "dropIn" | "trial" | "term" | "appointment" | "rental";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
      /** Stamped by the orchestrator from the ticket's V2 row (request-to-book). */
      requiresApproval?: boolean;
      /** Stamped too: the deposit is paid when the request is sent. */
      depositAtRequest?: boolean;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
      groupId?: string | null;
      groupName?: string | null;
    }>;
    appointmentData?: {
      startTime: string;
      endTime: string;
      duration: number;
      fractions?: number;
    };
    /** The LISTING's timezone, so every basket surface renders the window on
     *  one clock. Declared here so the compiler enforces it: the app handlers
     *  copy this payload field by field, and an undeclared field is dropped in
     *  silence — which is how it went missing the first time. */
    timezone?: string;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    /** Attendees from the participants step (existing → id only; inline → answers). */
    participants?: ParticipantPayloadEntry[];
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /**
   * Callback when user clicks "Proceed to Checkout" in the BasketChoiceStep.
   * Adds the item to basket and navigates to the basket checkout page.
   */
  onProceedToBasketCheckout: (itemData: {
    type: "booking";
    bookingType?: "regular" | "dropIn" | "trial" | "term" | "appointment" | "rental";
    courseId: string;
    courseName: string;
    sessionId?: string;
    sessionDate: string;
    ticketSelections: Array<{
      ticketTypeId: string;
      ticketTypeName: string;
      quantity: number;
      price: number;
      /** Stamped by the orchestrator from the ticket's V2 row (request-to-book). */
      requiresApproval?: boolean;
      /** Stamped too: the deposit is paid when the request is sent. */
      depositAtRequest?: boolean;
    }>;
    addOns?: Array<{
      addOnId: string;
      addOnName: string;
      quantity: number;
      price: number;
      groupId?: string | null;
      groupName?: string | null;
    }>;
    appointmentData?: {
      startTime: string;
      endTime: string;
      duration: number;
      fractions?: number;
    };
    /** The LISTING's timezone, so every basket surface renders the window on
     *  one clock. Declared here so the compiler enforces it: the app handlers
     *  copy this payload field by field, and an undeclared field is dropped in
     *  silence — which is how it went missing the first time. */
    timezone?: string;
    userInputs?: Array<{
      index: number;
      guestIndex: number | null;
      label: string;
      value: string | boolean | Date | null;
    }>;
    /** Attendees from the participants step (existing → id only; inline → answers). */
    participants?: ParticipantPayloadEntry[];
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    isOnline?: boolean;
  }) => void;
  /**
   * Whether the current user is logged in.
   * When true, enables "Pay with Bundle" by fetching user bundles.
   * When false and onLogin is provided, shows a "Log in to use bundles" prompt.
   */
  isLoggedIn?: boolean;
  /**
   * Callback to trigger the login flow.
   * When provided (and user is not logged in), a "Log in" button is shown in payment methods.
   * Not available in webcomponents (no login capability).
   */
  onLogin?: () => void;
  /**
   * When true, the school requires customers to be signed in before checkout.
   * Combined with `!isLoggedIn` and a provided `onLogin`, the orchestrator
   * renders a login gate in place of the flow content once a booking option
   * is selected. Fails open when `onLogin` is absent (e.g. webcomponents) so
   * the customer is never dead-ended. Gift-card checkout is exempt (it does
   * not route through this orchestrator).
   */
  requireLogin?: boolean;
  /**
   * When true, skip the basket choice step and auto-fire onProceedToBasketCheckout.
   * Used by webcomponents where there is no basket drawer to review items in.
   */
  directCheckout?: boolean;
  /**
   * When true, shows a helper explaining how bundle/membership credits
   * are applied at checkout. Intended for widget use only (not webcomponents).
   */
  showBundleMembershipHelp?: boolean;
};

/**
 * CheckoutOrchestrator - Shared checkout flow orchestration component
 *
 * This component handles:
 * - Fetching course data on mount
 * - Displaying booking options grid
 * - Handling option selection → specific checkout flow
 * - Providing API callbacks to checkout components
 * - Calling onSuccess after successful checkout
 */
export function CheckoutOrchestrator({
  listingId,
  apiConfig,
  primaryColor,
  onClose,
  onSuccess,
  onError,
  renderWrapper,
  renderHeader,
  allowInlineReceipt = false,
  closeOverlayOnPanelDismiss = false,
  enablePanelBackButton = false,
  onStepChange,
  isEmbedMode = false,
  initialBookingType,
  initialSessionId,
  offerRef,
  initialBundleId,
  isDirectBooking: isDirectBookingProp,
  onJoinWaitingList,
  onOpenTermsDialog,
  isMultiDateEnabled = false,
  maxDateSelections = 10,
  basketItemCount = 0,
  basketConflict = null,
  reservedSessionQuantities,
  existingParticipants = [],
  customer,
  buyerSelfAnswers,
  onBuyerSelfAnswersChange,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  isLoggedIn = false,
  onLogin,
  requireLogin = false,
  showBundleMembershipHelp = false,
}: CheckoutOrchestratorProps) {
  // Determine if this is a direct booking (from timetable "Book Now")
  // Either explicitly set via prop, or auto-detected when both initialBookingType and initialSessionId are provided
  const isDirectBooking = isDirectBookingProp ?? (!!initialBookingType && !!initialSessionId);
  const hasAppliedInitialRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [courseData, setCourseData] =
    useState<CourseWithBundlesResponse | null>(null);
  const [participantFields, setParticipantFields] = useState<CheckoutParticipantField[]>([]);
  const [activeCheckout, setActiveCheckout] = useState<ActiveCheckout>(null);

  // The brand colour, when the caller names one: the theme provider writes
  // it (and nothing else) inline. Without one the checkout draws in the
  // surface's own variables — a web component's :host defaults, or what its
  // page set on the element (Website v2's bookingComponentCss).
  const customTheme: PartialCheckoutTheme | undefined = primaryColor
    ? {
        colors: {
          primary: primaryColor,
          primaryHover: adjustColor(primaryColor, -10),
          primaryForeground: "#ffffff",
        },
      }
    : undefined;

  // Fetch course data on mount, and re-fetch when the buyer's identity
  // changes (the auth session can resolve just after mount, or the user can
  // log in mid-checkout). Identity determines which tickets the API reveals
  // (tag-gated / members-only). The loader owns the racy lifecycle —
  // sequence-guarded responses, a loading gate on the first load only, and
  // error surfacing/clearing — see course-data-loader.ts.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  const courseDataLoaderRef = useRef<ReturnType<
    typeof createCourseDataLoader
  > | null>(null);
  const loaderListingIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!courseDataLoaderRef.current || loaderListingIdRef.current !== listingId) {
      loaderListingIdRef.current = listingId;
      courseDataLoaderRef.current = createCourseDataLoader(listingId, {
        onLoadingChange: setIsLoading,
        onData: setCourseData,
        onParticipantFields: setParticipantFields,
        onErrorMessage: (message) => {
          setError(message);
          if (message !== null) onErrorRef.current?.(message);
        },
      });
    }
    courseDataLoaderRef.current.ensureLoaded(apiConfig);
  }, [listingId, apiConfig]);

  // Equipment (resource-pool) availability — advisory caps for the pickers.
  // One optimistic [now, now+90d] fetch per pool-bound ticket, in parallel,
  // fired as soon as the course payload identifies them. FAIL OPEN
  // everywhere: no pool tickets / failed fetch / endpoint not deployed yet /
  // session beyond the range → uncapped, never 0. The reserve/charge paths
  // stay authoritative.
  const [poolAvailability, setPoolAvailability] =
    useState<CheckoutPoolAvailability | null>(null);
  useEffect(() => {
    const poolTickets = (courseData?.ticketTypesV2 || []).filter(
      (t: any) => t?.resourcePoolId,
    );
    if (!poolTickets.length || !listingId) {
      setPoolAvailability(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const now = new Date();
      const to = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      const entries = await Promise.all(
        poolTickets.map(async (t: any) => {
          const res = await fetchPoolAvailability(
            listingId,
            String(t.id),
            apiConfig,
            { from: now.toISOString(), to: to.toISOString() },
          );
          return [String(t.id), res] as const;
        }),
      );
      if (cancelled) return;
      const capsByTicket: CheckoutPoolAvailability["capsByTicket"] = {};
      const termAggregatesByTicket: CheckoutPoolAvailability["termAggregatesByTicket"] =
        {};
      for (const [id, res] of entries) {
        const caps = poolCapsByKey(res);
        if (caps) capsByTicket[id] = caps;
        if (res?.termAggregates) termAggregatesByTicket[id] = res.termAggregates;
      }
      setPoolAvailability(
        Object.keys(capsByTicket).length || Object.keys(termAggregatesByTicket).length
          ? { capsByTicket, termAggregatesByTicket }
          : null,
      );
    })();
    return () => {
      cancelled = true;
    };
    // apiConfig.baseUrl, NOT apiConfig. The widget builds apiConfig as an object
    // literal in its render body, so its identity changes on every parent
    // render — session resolve, analytics, any overlay state — and depending on
    // the object re-fired one request per pool-bound ticket each time, on the
    // checkout's hottest path, against an endpoint documented as "never make it
    // per-window". baseUrl is the only field this request reads
    // (fetchPoolAvailability -> getBaseUrl), and anything that changes WHICH
    // tickets are pooled arrives through courseData, which is already a
    // dependency — so this is narrower without being weaker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData, listingId, apiConfig?.baseUrl]);

  // Apply initial selection after data loads (for deep links)
  useEffect(() => {
    if (
      !courseData ||
      !initialBookingType ||
      hasAppliedInitialRef.current ||
      isLoading
    ) {
      return;
    }

    hasAppliedInitialRef.current = true;

    // Build initial checkout data
    const initialData: Record<string, unknown> = {};

    if (initialSessionId) {
      initialData.sessionId = initialSessionId;
    }

    if (initialBundleId) {
      initialData.bundleId = initialBundleId;
    }

    setActiveCheckout({
      type: initialBookingType,
      data: Object.keys(initialData).length > 0 ? initialData : undefined,
    });
  }, [
    courseData,
    initialBookingType,
    initialSessionId,
    initialBundleId,
    isLoading,
  ]);

  // Handle option selection
  const handleSelectOption = useCallback(
    (type: BookingOptionType, data?: Record<string, unknown>) => {
      setActiveCheckout({ type, data });
    },
    [],
  );

  // Handle checkout close (go back to options)
  const handleCheckoutClose = useCallback(() => {
    setActiveCheckout(null);
  }, []);

  // Handle checkout complete
  const handleCheckoutComplete = useCallback(() => {
    onSuccess();
  }, [onSuccess]);

  // API callback: Load more dates for regular checkout
  const handleLoadMoreDates = useCallback(
    async ({
      page,
      limit,
      sessionId,
      fromDate,
      locationId,
    }: {
      page: number;
      limit: number;
      sessionId?: string;
      fromDate?: string;
      locationId?: string;
    }) => {
      return loadMoreDates(
        listingId,
        apiConfig,
        { page, limit },
        { sessionId, fromDate, locationId, earlyAccess: apiConfig.earlyAccess, offer: offerRef },
      );
    },
    [listingId, apiConfig, offerRef],
  );

  // API callback: Load more term dates for term checkout
  // Uses /term-dates endpoint which returns one session per term (event),
  // NOT individual sessions within a term
  const handleLoadMoreTermDates = useCallback(
    async ({ page, limit, sessionId }: { page: number; limit: number; sessionId?: string }) => {
      return loadMoreTermDates(
        listingId,
        apiConfig,
        { page, limit },
        { sessionId, earlyAccess: apiConfig.earlyAccess, offer: offerRef },
      );
    },
    [listingId, apiConfig, offerRef],
  );

  // API callback: Load more trial dates for trial checkout
  // Uses /trial-dates endpoint which only returns sessions from terms with isTrialEnabled = true
  const handleLoadMoreTrialDates = useCallback(
    async ({ page, limit, sessionId }: { page: number; limit: number; sessionId?: string }) => {
      return loadMoreTrialDates(
        listingId,
        apiConfig,
        { page, limit },
        { sessionId, earlyAccess: apiConfig.earlyAccess },
      );
    },
    [listingId, apiConfig],
  );

  // API callback: Load more drop-in dates for drop-in checkout
  // Uses /drop-in-dates endpoint which only returns sessions from terms with isDropInEnabled = true
  const handleLoadMoreDropInDates = useCallback(
    async ({ page, limit, sessionId }: { page: number; limit: number; sessionId?: string }) => {
      return loadMoreDropInDates(
        listingId,
        apiConfig,
        { page, limit },
        { sessionId, earlyAccess: apiConfig.earlyAccess },
      );
    },
    [listingId, apiConfig],
  );

  // API callback: Confirm free booking
  const handleConfirmFreeBooking = useCallback(
    async (data: Record<string, unknown>) => {
      const pricingSummary = data._pricingSummary;
      delete data._pricingSummary;

      if (pricingSummary && apiConfig.onAnalytics) {
        apiConfig.onAnalytics("pricing_summary", pricingSummary);
      }

      // Inject school context — orchestrator hook getBookingData() doesn't
      // emit schoolId/schoolName but the basket item requires schoolId or
      // CreateBasketCheckoutSession 404s on `School.findById`.
      if (courseData?.school) {
        data.schoolId = courseData.school._id;
        data.schoolName = courseData.school.name;
      }

      const result = await confirmFreeBooking(data, apiConfig, {
        processImmediately: allowInlineReceipt,
      });

      // Widget mode: poll for the resulting booking record and redirect to
      // the classroom (or other type-specific URL). Webcomponent
      // (allowInlineReceipt) mode returns immediately and shows the
      // orchestrator's success screen; the booking is created asynchronously
      // by Inngest.
      if (result.basketOrderId && !allowInlineReceipt) {
        const record = await pollBasketOrderRecord(
          result.basketOrderId,
          apiConfig.baseUrl || "https://api.baluu.io",
        );
        const returnOrigin =
          typeof window !== "undefined"
            ? `${window.location.protocol}//${window.location.host}`
            : "";
        const checkoutMode = (data.checkoutMode as string) || undefined;
        window.location.href = buildPostSuccessUrl(record, returnOrigin, {
          checkoutMode,
        });
      }

      return result;
    },
    [apiConfig, allowInlineReceipt, courseData?.school],
  );

  // API callback: Validate discount
  const handleValidateDiscount = useCallback(
    async (code: string, email: string): Promise<DiscountResult> => {
      const isBundle = activeCheckout?.type === "bundle";
      const bundleId = activeCheckout?.data?.bundleId as string | undefined;
      return validateDiscount(
        code,
        email,
        isBundle && bundleId ? bundleId : listingId,
        apiConfig,
        {
          schoolId: courseData?.school?._id,
          typeCheck: isBundle ? "bundlev2" : undefined,
        },
      );
    },
    [listingId, apiConfig, courseData?.school?._id, activeCheckout],
  );

  // API callback: Load available slots for appointments/groupSchedule
  const handleLoadAvailableSlots = useCallback(
    async (
      month: number,
      year: number,
      ticketTypeId?: string,
      locationId?: string,
    ) => {
      // Pass booking type to API so it uses correct schedule slots
      const bookingType = activeCheckout?.type;
      // Equipment caps for EVERY pool-bound ticket, but only when the flow
      // could not name one. With a single ticket id the server's original
      // block already computes the cap and also drops exhausted slots; asking
      // for both would duplicate five queries per ticket for nothing.
      // The grid's OWN ticket set, not a second predicate. Re-deriving this as
      // a strict bookingType match dropped almost everything it should have
      // kept: "groupSchedule" is a booking-option type that no ticket ever
      // carries, and tickets attached to an appointment slot routinely default
      // to "regular" — three server paths create them that way. Asking for
      // every ticket on the listing was wrong in the other direction: those ids
      // cost five queries each and occupy places in the server's eight-id cap,
      // so real appointment tickets past that point lost their cap silently,
      // which is the oversell direction. getFilteredTicketTypesV2 already
      // encodes the slot-id fallback and the "regular" default, and it is what
      // feeds this very grid.
      //
      // Skipped entirely for variable-duration listings: their cap map is
      // gated off in the flow, so every id sent would buy five queries whose
      // answer is then discarded. Bounded client-side too — the server keeps
      // only the first eight and says nothing about the rest, which would
      // leave those tickets uncapped in the OVERSELL direction, so truncate
      // where it can at least be logged.
      // The FLOW's rule, shared rather than restated — it has drifted three
      // times when written out by hand here. Skipping caps for a mode the flow
      // does not actually run in withholds them from a surface that consumes
      // them, which is the oversell direction.
      const skipPoolCaps = isVariableDurationFlow({
        course: courseData?.course,
        isGroupSchedule: bookingType === "groupSchedule",
      });
      const poolTicketTypeIds =
        ticketTypeId || skipPoolCaps
          ? undefined
          : (() => {
              const ids = getFilteredTicketTypesV2(
                bookingType === "groupSchedule"
                  ? "appointment"
                  : bookingType || "appointment",
              )
                .filter((t: any) => t?.resourcePoolId)
                .map((t: any) => String(t.id));
              if (ids.length > MAX_POOL_CAP_TICKET_IDS) {
                console.warn(
                  `[checkout] ${ids.length} pool-bound tickets on this listing; ` +
                    `the equipment cap covers the first ${MAX_POOL_CAP_TICKET_IDS}. ` +
                    `Uncapped: ${ids.slice(MAX_POOL_CAP_TICKET_IDS).join(", ")}`,
                );
              }
              return ids.slice(0, MAX_POOL_CAP_TICKET_IDS);
            })();
      return fetchAvailableSlots(
        listingId,
        month,
        year,
        apiConfig,
        bookingType,
        ticketTypeId,
        locationId,
        poolTicketTypeIds,
      );
    },
    [listingId, apiConfig, activeCheckout?.type, courseData],
  );

  // API callback: equipment availability for ONE rental day.
  //
  // Separate from the session-mode `poolAvailability` state above, which is
  // useless to rentals: that fetch asks for REGULAR sessions over 90 days, a
  // rental listing has none, and the controller short-circuits to {} — i.e.
  // uncapped — however it is wired. Rentals need the route's `date` +
  // `durationMinutes` grid mode, keyed off a day and a duration the flow owns.
  //
  // Does NOT pass excludeReservationIds, deliberately. The ids live in the
  // host's basket hook and never reach this component, and reservations are
  // only created at checkout-page mount — a buyer in the picker holds nothing
  // yet unless they reached checkout and came back. Counting their own hold
  // under-offers, which is the safe direction.
  //
  // apiConfig.baseUrl, NOT apiConfig, for the reason documented on the pool
  // effect above: the widget builds apiConfig as an object literal in its
  // render body, so depending on the whole object makes this callback change
  // identity every render and re-fire the consumer's effect.
  const handleLoadPoolAvailability = useCallback(
    async (params: {
      ticketTypeId: string;
      dateISO: string;
      durationMinutes: number;
      startsISO?: string[];
    }) =>
      fetchPoolAvailability(listingId, params.ticketTypeId, apiConfig, {
        date: params.dateISO,
        durationMinutes: params.durationMinutes,
        starts: params.startsISO,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listingId, apiConfig?.baseUrl],
  );

  // Every flow builds its own ticketSelections; stamp request-to-book on them
  // here, once, so the host basket knows which items are requests. Memoised so
  // the flows see stable callbacks, as they did before the wrap.
  const v2TicketsForStamp = courseData?.ticketTypesV2;
  const stampedOnAddToBasket = useMemo(
    () =>
      onAddToBasket
        ? (itemData: Parameters<typeof onAddToBasket>[0]) =>
            onAddToBasket(stampRequiresApproval(itemData, v2TicketsForStamp ?? []))
        : undefined,
    [onAddToBasket, v2TicketsForStamp],
  );
  const stampedOnProceedToBasketCheckout = useCallback(
    (itemData: Parameters<typeof onProceedToBasketCheckout>[0]) =>
      onProceedToBasketCheckout(stampRequiresApproval(itemData, v2TicketsForStamp ?? [])),
    [onProceedToBasketCheckout, v2TicketsForStamp],
  );

  // Build booking options
  const bookingOptions = courseData
    ? buildBookingOptions({
        course: courseData.course,
        bundles: courseData.bundles || [],
        ticketTypes: courseData.ticketTypes || [],
        ticketTypesV2: courseData.ticketTypesV2 || [],
        onSelect: handleSelectOption,
      })
    : [];

  // Ref to track if we've auto-selected
  const hasAutoSelectedRef = useRef(false);

  // Auto-select when there's only one booking option
  useEffect(() => {
    if (
      !isLoading &&
      !activeCheckout &&
      bookingOptions.length === 1 &&
      !hasAutoSelectedRef.current
    ) {
      hasAutoSelectedRef.current = true;
      // Schedule auto-select after render to avoid state update during render
      const option = bookingOptions[0];
      if (option.onSelect) {
        setTimeout(() => {
          option.onSelect();
        }, 0);
      }
    }
  }, [isLoading, activeCheckout, bookingOptions]);

  // Filter ticket types based on checkout type
  const getFilteredTicketTypes = (bookingType: string) => {
    const allTicketTypes = courseData?.ticketTypes || [];

    if (bookingType === "appointment") {
      const slotTicketIds = new Set<string>();
      for (const slot of courseData?.course?.appointmentScheduleSlots || []) {
        for (const ticket of slot.tickets || []) {
          for (const id of ticket.ticketTypes || []) {
            slotTicketIds.add(id);
          }
        }
      }
      return allTicketTypes.filter(
        (tt: { _id: string; bookingType: string }) =>
          tt.bookingType === bookingType || slotTicketIds.has(tt._id),
      );
    }

    return allTicketTypes.filter(
      (tt: { bookingType: string }) => tt.bookingType === bookingType,
    );
  };

  // Filter V2 ticket types based on booking type
  const getFilteredTicketTypesV2 = (bookingType: string) => {
    const allV2 = courseData?.ticketTypesV2 || [];

    if (bookingType === "appointment") {
      const slotTicketIds = new Set<string>();
      for (const slot of courseData?.course?.appointmentScheduleSlots || []) {
        for (const ticket of slot.tickets || []) {
          for (const id of ticket.ticketTypes || []) {
            slotTicketIds.add(id);
          }
        }
      }
      return allV2.filter(
        (tt: { id?: string; bookingType?: string }) =>
          (tt.bookingType || "regular") === bookingType ||
          slotTicketIds.has(tt.id || ""),
      );
    }

    return allV2.filter(
      (tt: { bookingType?: string }) =>
        (tt.bookingType || "regular") === bookingType,
    );
  };

  // Render the appropriate checkout flow
  const renderActiveCheckout = () => {
    if (!activeCheckout || !courseData) return null;

    const {
      course,
      school,
      locations,
      ticketTypes,
      ticketTypesV2,
      priorPurchasedTicketIds,
      customerBookingHistory,
      cartPricingRules,
      tutors,
      allAddOns,
      allAddOnGroups,
      unavailableTimeBlocks,
      bundles,
    } = courseData;

    // Extract bundleId from activeCheckout data
    // Bundle bookings bypass basket — bundles have their own payment/pricing logic
    const bundleId = activeCheckout.data?.bundleId as string | undefined;
    // Find the bundle object if bundleId is provided
    const bundle = bundleId
      ? courseData?.bundle || bundles?.find((b: any) => b._id === bundleId || b.id === bundleId)
      : undefined;

    // Common props for all checkout flows
    // When closeOverlayOnPanelDismiss is true, close the overlay entirely instead of going back to options
    const commonProps = {
      onClose: closeOverlayOnPanelDismiss ? onClose : handleCheckoutClose,
      // Bundle props for bundle-based bookings
      bundleID: bundleId,
      bundle,
      onSuccess: handleCheckoutComplete,
      onError: (error: Error | string) => {
        console.error("Checkout error:", error);
        onError?.(error);
      },
      onConfirmFreeBooking: handleConfirmFreeBooking,
      onValidateDiscount: handleValidateDiscount,
      currency: course.currency || "GBP",
      timezone: course.timezone,
      cancellationPolicyUrl: school.cancellationPolicyUrl || "/terms",
      school: {
        name: school.name,
        _id: school._id,
        isNewsletterSignupEnabled: school.isNewsletterSignupEnabled,
        customNewsletterSignupText: school.customNewsletterSignupText,
        businessCountry: school.businessCountry,
      },
      // Participants (attendees) — school flags from the course API, field defs
      // self-fetched; existing participants forwarded from the host when
      // signed in. Flow into every flow via {...commonProps}.
      participantsEnabled: !!school.participantsEnabled,
      participantLabel: school.participantLabel || "Attendees",
      participantFields,
      existingParticipants,
      buyerIsAttendee: !!school.buyerIsAttendee,
      // Name-later listing (plans-done/booking-seats-whos-coming.md): empty
      // attendee places are accepted and named afterwards.
      allowUnnamedAttendees: !!school.allowUnnamedAttendees,
      customer,
      selfSeed: buyerSelfAnswers,
      onSelfAnswersChange: onBuyerSelfAnswersChange,
      // Terms settings for inline dialog display — the API returns the resolved
      // object (incl. hasMultiplePolicies) as school.settings, so pass it through.
      termsSettings: school?.settings,
      customTerms: school?.customTerms,
      // Callback for webcomponents to open terms dialog in Shadow DOM portal
      onOpenTermsDialog,
      // Terms and conditions checkbox requirement - stored in school settings, not course
      mandatoryTickBox: school?.settings?.termsAndConditions?.mandatoryTickBox ?? false,
      // Allow users to fill in other guests' details (from course settings)
      allowPrefillOthersGuestsOnCheckout: course.allowPrefillOthersGuestsOnCheckout ?? false,
      // Step change callback for scroll-to-top behavior
      onStepChange,
      // Embed mode: non-sticky footer for iframe embeds
      isEmbedMode,
      // Direct booking mode: hides back navigation to options/dates (timetable Book Now)
      isDirectBooking,
      // Multi-date selection
      isMultiDateEnabled,
      maxDateSelections,
      // Basket feature props
      basketItemCount,
      basketConflict,
      // Basket-aware spots-left feed
      reservedSessionQuantities,
      onAddToBasket: stampedOnAddToBasket,
      onProceedToBasketCheckout: stampedOnProceedToBasketCheckout,
      directCheckout,
      // Waiting list callback - passed to DateSelectionStep for "Can't make these dates?" callout
      onJoinWaitlist: onJoinWaitingList,
      // Login callback - shown in payment methods when user is not logged in (widget only, not webcomponents)
      onLogin: !isLoggedIn ? onLogin : undefined,
      // Bundle/membership helper
      showBundleMembershipHelp,
    };

    switch (activeCheckout.type) {
      case "term": // Unified term checkout
      case "fullTerm": // @deprecated - kept for backwards compatibility
      case "proRataTerm": // @deprecated - kept for backwards compatibility
        return (
          <CheckoutTerm
            course={
              course as unknown as Parameters<typeof CheckoutTerm>[0]["course"]
            }
            locations={locations}
            tutors={
              tutors as unknown as Parameters<typeof CheckoutTerm>[0]["tutors"]
            }
            ticketTypes={
              getFilteredTicketTypes("regular") as unknown as Parameters<
                typeof CheckoutTerm
              >[0]["ticketTypes"]
            }
            ticketTypesV2={ticketTypesV2}
            priorPurchasedTicketIds={priorPurchasedTicketIds}
            customerBookingHistory={customerBookingHistory}
            cartPricingRules={cartPricingRules}
            addOns={
              allAddOns as unknown as Parameters<
                typeof CheckoutTerm
              >[0]["addOns"]
            }
            addOnGroups={allAddOnGroups as any[]}
            initialSessionId={initialSessionId}
            poolAvailability={poolAvailability}
            onLoadMoreDates={handleLoadMoreTermDates}
            {...commonProps}
          />
        );

      case "regular":
        return (
          <CheckoutRegular
            course={
              course as unknown as Parameters<
                typeof CheckoutRegular
              >[0]["course"]
            }
            locations={locations}
            tutors={
              tutors as unknown as Parameters<
                typeof CheckoutRegular
              >[0]["tutors"]
            }
            ticketTypes={
              getFilteredTicketTypes("regular") as unknown as Parameters<
                typeof CheckoutRegular
              >[0]["ticketTypes"]
            }
            ticketTypesV2={ticketTypesV2}
            priorPurchasedTicketIds={priorPurchasedTicketIds}
            customerBookingHistory={customerBookingHistory}
            cartPricingRules={cartPricingRules}
            addOns={
              allAddOns as unknown as Parameters<
                typeof CheckoutRegular
              >[0]["addOns"]
            }
            addOnGroups={allAddOnGroups as any[]}
            initialSessionId={initialSessionId}
            onLoadMoreDates={handleLoadMoreDates}
            poolAvailability={poolAvailability}
            {...commonProps}
          />
        );

      case "trial":
        return (
          <CheckoutTrial
            course={
              course as unknown as Parameters<typeof CheckoutTrial>[0]["course"]
            }
            locations={locations}
            ticketType={
              ticketTypes.find(
                (tt: { _id: string }) => tt._id === course.trialTicketTypeId,
              ) as unknown as Parameters<typeof CheckoutTrial>[0]["ticketType"]
            }
            initialSessionId={initialSessionId}
            onLoadMoreDates={handleLoadMoreTrialDates}
            {...commonProps}
          />
        );

      case "dropIn":
        return (
          <CheckoutDropIn
            course={
              course as unknown as Parameters<
                typeof CheckoutDropIn
              >[0]["course"]
            }
            locations={locations}
            ticketType={
              ticketTypes.find(
                (tt: { _id: string }) => tt._id === course.dropInTicketTypeId,
              ) as unknown as Parameters<typeof CheckoutDropIn>[0]["ticketType"]
            }
            initialSessionId={initialSessionId}
            onLoadMoreDates={handleLoadMoreDropInDates}
            {...commonProps}
          />
        );

      case "appointment":
        return (
          <CheckoutAppointment
            course={
              course as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["course"]
            }
            ticketTypes={
              getFilteredTicketTypes("appointment") as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["ticketTypes"]
            }
            ticketTypesV2={getFilteredTicketTypesV2("appointment")}
            priorPurchasedTicketIds={priorPurchasedTicketIds}
            customerBookingHistory={customerBookingHistory}
            cartPricingRules={cartPricingRules}
            unavailableTimeBlocks={
              unavailableTimeBlocks as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["unavailableTimeBlocks"]
            }
            addOns={
              allAddOns as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["addOns"]
            }
            addOnGroups={allAddOnGroups as any[]}
            onLoadAvailableSlots={handleLoadAvailableSlots}
            {...commonProps}
          />
        );

      case "rental":
        return (
          <CheckoutRental
            course={course}
            ticketTypesV2={getFilteredTicketTypesV2("rental")}
            // The FULL rental-ticket universe, not the buyer-filtered list
            // above — see the field's note in types.ts.
            rentalTicketIds={courseData?.rentalTicketIds}
            onLoadPoolAvailability={handleLoadPoolAvailability}
            {...commonProps}
          />
        );

      case "bundle":
        const bundleData = activeCheckout.data as {
          bundle?: BundleData;
          usingBundlesV2?: boolean;
        };
        const selectedBundle = bundleData?.bundle || bundles?.[0];
        if (!selectedBundle) return null;

        return (
          <CheckoutBundle
            {...commonProps}
            course={
              course as unknown as Parameters<
                typeof CheckoutBundle
              >[0]["course"]
            }
            bundle={
              selectedBundle as unknown as Parameters<
                typeof CheckoutBundle
              >[0]["bundle"]
            }
          />
        );

      case "groupSchedule":
        // Group schedule uses appointment checkout with group ticket types
        return (
          <CheckoutAppointment
            course={
              course as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["course"]
            }
            ticketTypes={
              getFilteredTicketTypes("group") as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["ticketTypes"]
            }
            ticketTypesV2={getFilteredTicketTypesV2("appointment")}
            priorPurchasedTicketIds={priorPurchasedTicketIds}
            customerBookingHistory={customerBookingHistory}
            cartPricingRules={cartPricingRules}
            unavailableTimeBlocks={
              unavailableTimeBlocks as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["unavailableTimeBlocks"]
            }
            addOns={
              allAddOns as unknown as Parameters<
                typeof CheckoutAppointment
              >[0]["addOns"]
            }
            addOnGroups={allAddOnGroups as any[]}
            onLoadAvailableSlots={handleLoadAvailableSlots}
            subtitle="Private Booking"
            isGroupSchedule
            {...commonProps}
          />
        );

      default:
        return null;
    }
  };

  // Default header. Its colours, and the frame's below, are the gray and
  // white they always were unless the host page dresses them through the
  // --checkout-frame* variables (lib/checkout/frame.ts; Website v2 does).
  const defaultHeader = (
    <header className={`sticky top-0 z-50 border-b ${CHECKOUT_FRAME_CLASSES.header}`}>
      <div className="container mx-auto px-1 sm:px-4 py-3 flex items-center justify-between">
        {/* Hide "Back to options" when in direct booking mode (from timetable Book Now) */}
        {activeCheckout && !isDirectBooking && (
          <button
            onClick={handleCheckoutClose}
            className={`font-medium text-sm ${CHECKOUT_FRAME_CLASSES.link}`}
          >
            ← Back to options
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={onClose}
          className={`p-2 rounded-full transition-colors ${CHECKOUT_FRAME_CLASSES.close}`}
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
    </header>
  );

  // Back affordance for the active flow's panel header (opt-in via
  // enablePanelBackButton). Single-option experiences auto-skip the options
  // grid, so Back has nowhere to return to → dismiss the overlay; with
  // multiple options, Back steps back to the grid.
  const panelBackHandler = enablePanelBackButton
    ? () => {
        if (bookingOptions.length <= 1) {
          onClose();
        } else {
          handleCheckoutClose();
        }
      }
    : undefined;

  const content = (
    <CheckoutThemeProvider theme={customTheme}>
      <CheckoutHeaderActionsProvider value={{ onBack: panelBackHandler }}>
        <div className={`relative w-full min-h-screen ${CHECKOUT_FRAME_CLASSES.frame}`}>
        {/* Header */}
        {renderHeader
          ? renderHeader({
              activeCheckout,
              onBack: handleCheckoutClose,
              onClose,
            })
          : defaultHeader}

        {/* Content */}
        {activeCheckout ? (
          // Render the active checkout flow - wrap in container.
          // When the school requires login and the customer is signed out,
          // show the login gate in place of the flow (before dates/tickets).
          // This also gates the in-flow "join waiting list" affordance, which
          // is intended: a school that requires sign-in to book requires it to
          // reach any flow. Anonymous waitlist joins remain available from the
          // un-gated booking-options grid below.
          <main className="container mx-auto px-1 sm:px-4 py-6 pb-24">
            {shouldGateCheckout({ requireLogin, isLoggedIn, onLogin }) &&
            onLogin ? (
              <CheckoutLoginGate
                schoolName={courseData?.school?.name}
                onLogin={onLogin}
              />
            ) : (
              renderActiveCheckout()
            )}
          </main>
        ) : (
          // Show booking options - BookingOptionsGrid/CheckoutPanel has its own container
          <BookingOptionsGrid
            options={bookingOptions}
            title={courseData?.course.title}
            thumbnail={courseData?.course.thumbnail}
            currency={courseData?.course.currency || "GBP"}
            isOnline={courseData?.course.isOnline || false}
            isLoading={isLoading}
            error={error}
            headerTitle="Choose a booking option"
            headerDescription="Select an option you'd like to book."
            onJoinWaitingList={
              onJoinWaitingList
                ? () => onJoinWaitingList(initialSessionId)
                : undefined
            }
            cancellationPolicyUrl={courseData?.school?.cancellationPolicyUrl || "/terms"}
            termsSettings={courseData?.school?.settings}
            customTerms={courseData?.school?.customTerms}
            timezone={courseData?.course?.timezone}
            onOpenTermsDialog={onOpenTermsDialog}
            showBundleMembershipHelp={
              !initialBundleId && showBundleMembershipHelp
            }
          />
        )}
        </div>
      </CheckoutHeaderActionsProvider>
    </CheckoutThemeProvider>
  );

  // Apply wrapper if provided
  return renderWrapper ? <>{renderWrapper(content)}</> : content;
}

/**
 * Adjust a hex color by a percentage
 */
function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000ff) + amt));
  return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
}

export type { ActiveCheckout, CheckoutApiConfig } from "../../lib/checkout";
